import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/feature/SEOMeta';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, articles } from '@/mocks/siteData';

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91n.com' },
  ],
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === activeCategory);

  useEffect(() => {
    if (slug && slug !== activeCategory) {
      setActiveCategory(slug);
    }
  }, [slug, activeCategory]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    navigate(`/category/${newSlug}`);
  };

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 91n.com 官方指南`,
    description: currentCategory.description,
    url: `https://91n.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '91n.com 官方入口与使用指南',
      url: 'https://91n.com',
    },
  };

  const currentBreadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91n.com' },
      { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `https://91n.com/category/${activeCategory}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${currentCategory.name} - 91n.com 官方指南`}
        description={currentCategory.description}
        keywords={`91n.com,${currentCategory.name},91n.com入口,91n.com使用指南,91n.com官网`}
        canonicalUrl={`https://91n.com/category/${activeCategory}`}
        breadcrumbs={[
          { name: '首页', path: '/' },
          { name: currentCategory.name, path: `/category/${activeCategory}` },
        ]}
        schemaData={[categorySchema, currentBreadcrumbSchema]}
      />

      <Navbar />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
              {currentCategory.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-600">
              {currentCategory.description} · 共 {categoryArticles.length} 篇文章
            </p>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="w-full px-4 md:px-6 border-b border-background-200/70 bg-background-50 sticky top-16 z-40">
          <div className="max-w-7xl mx-auto flex items-center gap-2 overflow-x-auto py-3 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                }`}
              >
                <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Article List */}
        <section className="w-full px-4 md:px-6 py-12 bg-background-50">
          <div className="max-w-7xl mx-auto">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:bg-background-200/70 cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs text-foreground-400">{article.readTime}</span>
                        <span className="text-xs text-foreground-400">{article.publishDate}</span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 font-heading group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-2 mt-4 flex-wrap">
                        {article.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-xs text-foreground-500 bg-background-200/70 px-2 py-0.5 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}